import { EllipsisIcon } from "@xola/icons";
import { Badge, Key, PopoverList, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { isEquipmentBased } from "common";
import { sumBy } from "lodash";
import React, { MutableRefObject, useMemo, useState } from "react";
import { Event, Experience, Permission, Timeslot } from "types";
import FullCalendar from "@fullcalendar/react";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { CreateEventNoteModal } from "../../Event/CreateEventNoteModal";
import { MessageSelectedEventsModal } from "../../Event/MessageSelectedEventsModal";
import { AssignGuidePopover } from "../Resources/Guides/AssignGuidePopover";
import { UnassignEventGuidePrompt } from "../Resources/Guides/UnassignEventGuides";
import { queryClient } from "@/api/queryClient";
import { ApplyExperienceOptionModal } from "@/components/Event/ApplyExperienceOptionModal";
import { BlackoutMultipleEventsModal } from "@/components/Event/BlackoutMultipleEventsModal";
import { CapMultipleEventsModal } from "@/components/Event/CapMultipleEventsModal";
import { RemoveExperienceOptionModal } from "@/components/Event/RemoveExperienceOptionModal";
import { useExperience } from "@/hooks/resources/useProduct";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useModal } from "@/hooks/useVisibility";
import { getOptionTargetEvents, getRemoveTargets, isSingleExperienceSelection } from "@/models/Event";
import { isOptionsEnabled } from "@/models/Experience";

interface SelectedEventsToolbarProps {
    events: Event[];
    timeslots: Timeslot[];
    calendarRef: MutableRefObject<FullCalendar | null>;
    onClearSelected: () => void;
}

const hasEquipmentBasedSelection = (events: Event[], timeslots: Timeslot[]): boolean =>
    [...events.map((event) => event.experience.id), ...timeslots.map((timeslot) => timeslot.product)].some(
        (experienceId) => {
            const experience = queryClient.getQueryData<Experience>(["products", experienceId]);
            return experience ? isEquipmentBased(experience) : false;
        },
    );

export const SelectedEventsToolbar = ({
    events,
    timeslots,
    calendarRef,
    onClearSelected,
}: SelectedEventsToolbarProps) => {
    const { isGranted } = useAuthorization();
    const [visible, setVisible] = useState(false);
    const { isVisible: showCapEventModal, toggle: toggleShowCapEventModal } = useModal(false);
    const { isVisible: showBlackoutEventModal, toggle: toggleShowBlackoutEventModal } = useModal(false);
    const { isVisible: showApplyExperienceOptionModal, toggle: toggleShowApplyExperienceOptionModal } = useModal(false);
    const { isVisible: showRemoveExperienceOptionModal, toggle: toggleShowRemoveExperienceOptionModal } =
        useModal(false);

    const [showMessageSelectedEventsModal, setShowMessageSelectedEventsModal] = useState(false);
    const [showAssignGuide, setShowAssignGuide] = useState(false);
    const [showEventAddNoteModal, setShowEventAddNoteModal] = useState(false);
    const [showUnassignGuide, setShowUnassignGuide] = useState(false);

    const selectedEvents = calendarRef?.current
        ?.getApi()
        .getEvents()
        .map((event) => event?.extendedProps?.selectedEvent)
        .filter((event) => event !== null && event !== undefined);

    const guides = selectedEvents?.map((event) => event.guides).filter((guides) => guides.length > 0) ?? [];

    const show = () => {
        setVisible(true);
    };

    const hide = () => {
        setVisible(false);
    };

    const handleMessage = () => {
        setShowMessageSelectedEventsModal(true);
        setVisible(false);
    };

    const handleAddEventNote = () => {
        setShowEventAddNoteModal(true);
        setVisible(false);
    };

    const handleGuideAssignment = () => {
        setShowAssignGuide(true);
        setVisible(false);
    };

    const handleGuideUnAssignment = () => {
        setShowUnassignGuide(true);
        setVisible(false);
    };

    const handleCapEvent = () => {
        if (hasEquipmentBasedEvents) {
            return;
        }
        toggleShowCapEventModal();
        setVisible(false);
    };

    const handleBlackoutEvent = () => {
        toggleShowBlackoutEventModal();
        setVisible(false);
    };

    const handleApplyExperienceOption = () => {
        toggleShowApplyExperienceOptionModal();
        setVisible(false);
    };

    const handleRemoveExperienceOption = () => {
        toggleShowRemoveExperienceOptionModal();
        setVisible(false);
    };

    const reservedCount = sumBy(events, "quantity.reserved");
    const isMac = navigator.userAgent.includes("Mac");

    const eventsForOption = useMemo(() => getOptionTargetEvents(events, timeslots), [events, timeslots]);
    // Experience Options can't cross Experiences - frontend-only gate, no backend enforcement.
    const isSingleExperience = isSingleExperienceSelection(eventsForOption);

    const { experience: selectionExperience } = useExperience(
        isSingleExperience ? eventsForOption[0].experience.id : undefined,
    );

    const canModifyBooking = isGranted(Permission.MODIFY_BOOKING);
    const hasEquipmentBasedEvents = hasEquipmentBasedSelection(events, timeslots);
    const canApplyExperienceOption = canModifyBooking && isSingleExperience && isOptionsEnabled(selectionExperience);
    const canRemoveExperienceOption =
        canModifyBooking &&
        getRemoveTargets(eventsForOption).length > 0 &&
        (!isSingleExperience || isOptionsEnabled(selectionExperience));

    return (
        <div
            className="fixed right-0 bottom-0 z-50 flex h-12 w-full items-center justify-between bg-blue-lighter py-3 px-10"
            data-testid="selected-events-toolbar-row"
        >
            <div className="flex items-center space-x-1">
                {isMac ? (
                    <Key char="cmd" className="no-translate !text-black" />
                ) : (
                    <Key char="ctrl" className="no-translate !text-black" />
                )}
                <Key char="Click" className="!text-black" />
                <span>to select multiple events</span>
            </div>
            <div className="flex space-x-2">
                <Badge
                    color="primary"
                    size="small"
                    className="bg-blue !text-white"
                    data-testid="selected-events-toolbar-events-count"
                >
                    {events.length + timeslots.length}
                </Badge>
                <span className="events-selected font-bold">Events Selected</span>

                <MessageSelectedEventsModal
                    eventIds={events.map((e) => e.id)}
                    isOpen={showMessageSelectedEventsModal}
                    onClose={() => setShowMessageSelectedEventsModal(false)}
                />

                <AssignGuidePopover
                    events={events ?? []}
                    target={<span />}
                    timeslots={timeslots}
                    isVisible={showAssignGuide}
                    onClose={() => setShowAssignGuide(false)}
                />

                <UnassignEventGuidePrompt
                    isMultiSelectEventsFromDashboard
                    isOpen={showUnassignGuide}
                    events={events ?? []}
                    onClose={() => setShowUnassignGuide(false)}
                />

                <CreateEventNoteModal
                    events={events}
                    timeslots={timeslots}
                    isOpen={showEventAddNoteModal}
                    onClose={() => setShowEventAddNoteModal(false)}
                />

                <CapMultipleEventsModal
                    events={events}
                    timeslots={timeslots}
                    isOpen={showCapEventModal}
                    onClose={() => {
                        toggleShowCapEventModal();
                        onClearSelected();
                    }}
                />

                <BlackoutMultipleEventsModal
                    events={events}
                    timeslots={timeslots}
                    isOpen={showBlackoutEventModal}
                    onClose={() => {
                        toggleShowBlackoutEventModal();
                        onClearSelected();
                    }}
                />

                <ApplyExperienceOptionModal
                    events={eventsForOption}
                    isOpen={showApplyExperienceOptionModal}
                    onClose={() => {
                        toggleShowApplyExperienceOptionModal();
                        onClearSelected();
                    }}
                />

                <RemoveExperienceOptionModal
                    events={eventsForOption}
                    isOpen={showRemoveExperienceOptionModal}
                    onClose={() => {
                        toggleShowRemoveExperienceOptionModal();
                        onClearSelected();
                    }}
                />

                <PopoverList
                    offset={[0, 18]}
                    visible={visible}
                    className="z-100 min-w-max opacity-100"
                    onClickOutside={hide}
                >
                    <SecondaryButton size="tiny" onClick={visible ? hide : show}>
                        <EllipsisIcon />
                    </SecondaryButton>
                    {reservedCount !== 0 && (
                        <PopoverList.Item name="list" onClickItem={handleMessage}>
                            <span>Message</span>
                        </PopoverList.Item>
                    )}
                    {reservedCount !== 0 && (
                        <PopoverList.Item name="note" onClickItem={handleAddEventNote}>
                            <span>Add Event Note</span>
                        </PopoverList.Item>
                    )}
                    {isGranted(Permission.RESOURCES_GUIDES_ALL_VIEW) && (
                        <PopoverList.Item name="guides" onClickItem={handleGuideAssignment}>
                            <span>Guides</span>
                        </PopoverList.Item>
                    )}
                    {isGranted(Permission.RESOURCES_GUIDES_ALL_VIEW) && guides.length > 0 && (
                        <PopoverList.Item name="unassign" onClickItem={handleGuideUnAssignment}>
                            <span>Unassign Guides</span>
                        </PopoverList.Item>
                    )}

                    {canModifyBooking && (
                        <PopoverList.Item
                            name="cap event"
                            data-testid="selected-events-toolbar-cap-event"
                            className={clsx(hasEquipmentBasedEvents && "opacity-50")}
                            onClickItem={handleCapEvent}
                        >
                            <Tooltip
                                content="Equipment-based events cannot be capped"
                                disabled={!hasEquipmentBasedEvents}
                            >
                                <span>Cap event</span>
                            </Tooltip>
                        </PopoverList.Item>
                    )}

                    {isGranted(Permission.MODIFY_BOOKING) && (
                        <PopoverList.Item
                            name="blackout"
                            data-testid="selected-events-toolbar-blackout"
                            onClickItem={handleBlackoutEvent}
                        >
                            <span>Blackout</span>
                        </PopoverList.Item>
                    )}

                    {canApplyExperienceOption && (
                        <PopoverList.Item
                            name="apply experience option"
                            data-testid="selected-events-toolbar-apply-option"
                            onClickItem={handleApplyExperienceOption}
                        >
                            <span className="whitespace-nowrap">Apply Experience Option</span>
                        </PopoverList.Item>
                    )}

                    {canRemoveExperienceOption && (
                        <PopoverList.Item
                            name="remove experience option"
                            data-testid="selected-events-toolbar-remove-option"
                            onClickItem={handleRemoveExperienceOption}
                        >
                            <span className="whitespace-nowrap">Remove Experience Option</span>
                        </PopoverList.Item>
                    )}
                </PopoverList>
            </div>
            <div className="flex">
                <SecondaryButton size="small" className="bg-gray-lighter" onClick={() => onClearSelected()}>
                    Clear All
                </SecondaryButton>
            </div>
        </div>
    );
};
